import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { apiErrorMessage } from '@/api/errors';
import { decideApproval, fetchApprovals } from '@/api/approvals';
import { formatMinor } from '@/api/commercial';
import type { ApprovalData } from '@/types/generated';

/**
 * The approval queue.
 *
 * A WORK QUEUE, NOT A REPORT. It defaults to what is waiting on the person reading it, oldest
 * first, with the figures on the row — an approver should be able to decide from the list without
 * opening each quotation to find out what they are agreeing to.
 *
 * Steps blocked behind an earlier approver are SHOWN, not hidden, with their buttons absent.
 * Hiding them would leave finance wondering whether the request had been lost; offering buttons
 * that 422 would teach everyone to ignore errors.
 */
export function ApprovalsPage() {
  const [mineOnly, setMineOnly] = useState(true);
  const [deciding, setDeciding] = useState<{ approval: ApprovalData; reject: boolean } | null>(
    null,
  );
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ['approvals', { mineOnly }],
    queryFn: ({ pageParam }) =>
      fetchApprovals({ scope: mineOnly ? 'mine' : 'all', cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });

  const approvals = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    // The quotation's own screen shows whether it is blocked, so it goes stale the moment a
    // decision lands here.
    void queryClient.invalidateQueries({ queryKey: ['quotation'] });
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h5">Approvals</Typography>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={mineOnly}
              onChange={(event) => setMineOnly(event.target.checked)}
            />
          }
          label="Waiting on me"
        />
      </Stack>

      {query.isError && <Alert severity="error">Could not load the approval queue.</Alert>}

      {query.isPending && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!query.isPending && approvals.length === 0 && (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {mineOnly
              ? 'Nothing waiting on you.'
              : 'No approvals yet. They appear when a quote trips one of this organisation’s thresholds.'}
          </Typography>
        </Card>
      )}

      <Stack spacing={1.5}>
        {approvals.map((approval) => (
          <ApprovalRow
            key={approval.id}
            approval={approval}
            onDecide={(reject) => setDeciding({ approval, reject })}
          />
        ))}
      </Stack>

      {query.hasNextPage && (
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}

      <DecisionDialog pending={deciding} onClose={() => setDeciding(null)} onDecided={refresh} />
    </Stack>
  );
}

function ApprovalRow({
  approval,
  onDecide,
}: {
  approval: ApprovalData;
  onDecide: (reject: boolean) => void;
}) {
  const decided = approval.status !== 'pending';

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Box
              component={RouterLink}
              to={`/quotations/${approval.quotation_id}`}
              sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}
            >
              {approval.quotation_label}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {approval.customer_name}
            </Typography>
            <Chip
              size="small"
              label={approval.required_role.replace(/_/g, ' ')}
              variant="outlined"
            />
            {approval.has_timed_out && (
              // Past its window. A fact about the process, not about the deal — the wording
              // avoids implying the customer is waiting on this specific hour.
              <Tooltip title="Nobody has looked at this within the time the policy allows.">
                <Chip size="small" color="error" label="overdue" />
              </Tooltip>
            )}
            {approval.delegated_to !== null && (
              <Chip
                size="small"
                variant="outlined"
                label={`delegated to ${approval.delegated_to}`}
              />
            )}
          </Stack>

          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {/* The figures, so a decision can be made from the list. */}
            {approval.summary}
            {approval.threshold_description !== null && (
              <Typography component="span" variant="body2" color="text.secondary">
                {' '}
                — {approval.threshold_description}
              </Typography>
            )}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            waiting {approval.waiting_hours}h{approval.step > 1 && ` · step ${approval.step}`}
            {!approval.is_actionable && !decided && ' · waiting on an earlier step'}
          </Typography>

          {approval.comments !== null && decided && (
            <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
              {approval.decided_by ?? 'Someone'}: {approval.comments}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          {decided ? (
            <Chip
              size="small"
              label={approval.status}
              color={
                approval.status === 'approved'
                  ? 'success'
                  : approval.status === 'rejected'
                    ? 'error'
                    : 'default'
              }
              sx={{ textTransform: 'capitalize' }}
            />
          ) : approval.is_actionable ? (
            <>
              <Button variant="contained" size="small" onClick={() => onDecide(false)}>
                Approve
              </Button>
              <Button variant="outlined" size="small" color="error" onClick={() => onDecide(true)}>
                Reject
              </Button>
            </>
          ) : (
            // Shown without buttons rather than hidden: the request exists and is not lost.
            <Chip size="small" variant="outlined" label="blocked" />
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

/**
 * The decision, with its mandatory reason on a rejection.
 *
 * A dialog rather than an inline button pair, because a rejection REQUIRES a reason — enforced by
 * the API, the domain action and a database constraint. The salesperson has to know what to
 * change, or they will simply go and ask in person and the decision leaves the audit trail.
 */
function DecisionDialog({
  pending,
  onClose,
  onDecided,
}: {
  pending: { approval: ApprovalData; reject: boolean } | null;
  onClose: () => void;
  onDecided: () => void;
}) {
  const [comments, setComments] = useState('');

  const decide = useMutation({
    mutationFn: () =>
      decideApproval(
        pending?.approval.id ?? '',
        pending?.reject ? 'rejected' : 'approved',
        comments,
      ),
    onSuccess: () => {
      setComments('');
      onClose();
      onDecided();
    },
  });

  const reject = pending?.reject ?? false;

  return (
    <Dialog open={pending !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{reject ? 'Send this back' : 'Approve this quote'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {pending?.approval.quotation_label} — {pending?.approval.summary}
          {pending?.approval.value_minor != null && pending.approval.currency != null && (
            <> ({formatMinor(pending.approval.value_minor, pending.approval.currency)})</>
          )}
        </DialogContentText>

        <TextField
          fullWidth
          multiline
          minRows={2}
          required={reject}
          label={reject ? 'Why?' : 'Note (optional)'}
          placeholder={
            reject ? 'e.g. margin below the floor for this lane — reprice at 8% off' : ''
          }
          helperText={reject ? 'The salesperson sees this. Say what would make it acceptable.' : ''}
          value={comments}
          onChange={(event) => setComments(event.target.value)}
        />

        {decide.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrorMessage(decide.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={reject ? 'error' : 'primary'}
          disabled={decide.isPending || (reject && comments.trim() === '')}
          onClick={() => decide.mutate()}
        >
          {reject ? 'Reject' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
